import type { MergedMarketRecord, NormalizedMarketRow, SourceId } from '@/types/market';
import { SOURCE_PRECEDENCE } from '@/data/sourceRegistry';

const MERGEABLE_NUMERIC_FIELDS: Array<keyof NormalizedMarketRow> = [
  'medianSalePrice',
  'medianSalePricePrevPeriod',
  'medianSalePriceYoyPct',
  'medianDaysOnMarket',
  'medianDaysOnMarketPrevPeriod',
  'activeListings',
  'activeListingsPrevPeriod',
  'monthsOfInventory',
  'monthsOfInventoryPrevPeriod',
  'closedSales',
  'closedSalesPrevPeriod',
  'newListings',
  'pendingSales',
  'saleToListRatioPct',
  'priceReductionSharePct',
  'medianHomeValueEstimate',
  'medianGrossRent',
  'homeownershipRatePct',
];

/**
 * Merges every source's row for one geography into a single resolved row,
 * field by field, using SOURCE_PRECEDENCE as the tiebreaker whenever more
 * than one source reports a value. A source that simply has no opinion on
 * a field (undefined) never overwrites a value already resolved from a
 * higher-precedence source.
 */
export function mergeGeographyRows(
  geoId: string,
  geoName: string,
  rowsBySource: Partial<Record<SourceId, NormalizedMarketRow>>,
): MergedMarketRecord {
  const contributingSources = SOURCE_PRECEDENCE.filter((id) => rowsBySource[id]);
  const base = rowsBySource[contributingSources[0]] ?? Object.values(rowsBySource)[0];

  if (!base) {
    throw new Error(`mergeGeographyRows: no rows supplied for ${geoId}`);
  }

  const resolved: NormalizedMarketRow = {
    ...base,
    geoId,
    geoName,
  };

  for (const field of MERGEABLE_NUMERIC_FIELDS) {
    for (const sourceId of SOURCE_PRECEDENCE) {
      const row = rowsBySource[sourceId];
      const value = row?.[field];
      if (value !== undefined) {
        (resolved as unknown as Record<string, unknown>)[field] = value;
        break;
      }
    }
  }

  // Field completeness reflects the *merged* row, not any single source.
  const filled = MERGEABLE_NUMERIC_FIELDS.filter((f) => resolved[f] !== undefined).length;
  resolved.fieldCompleteness = filled / MERGEABLE_NUMERIC_FIELDS.length;

  return {
    geoLevel: base.geoLevel,
    geoId,
    geoName,
    periodLabel: base.periodLabel,
    rowsBySource,
    resolved,
    contributingSources,
  };
}

/** Groups every adapter's rows by geoId and merges each group. */
export function buildMergedRecords(
  rowsBySourceList: Array<{ sourceId: SourceId; rows: NormalizedMarketRow[] }>,
): MergedMarketRecord[] {
  const byGeo = new Map<string, Partial<Record<SourceId, NormalizedMarketRow>>>();
  const nameByGeo = new Map<string, string>();

  for (const { sourceId, rows } of rowsBySourceList) {
    for (const row of rows) {
      if (!byGeo.has(row.geoId)) byGeo.set(row.geoId, {});
      byGeo.get(row.geoId)![sourceId] = row;
      if (!nameByGeo.has(row.geoId)) nameByGeo.set(row.geoId, row.geoName);
    }
  }

  const merged: MergedMarketRecord[] = [];
  for (const [geoId, rowsBySource] of byGeo) {
    merged.push(mergeGeographyRows(geoId, nameByGeo.get(geoId) ?? geoId, rowsBySource));
  }
  return merged;
}
